import {
  ComparatorContext,
  FormField,
  KiziLogger,
  parseTransformer,
  transformers as transformersMap,
  useField,
  useFormService,
  validate,
  validators,
} from '@kizi/comparator-core';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Address, AddressInputProps, AddressProps } from '../../Address';

type AddressWithApiCallInputProps = AddressInputProps & {
  name: string;
  validator?: FormField['validator'];
  transformers?: FormField['transformers'];
};

export type ApiSuccessParam = AddressApiSuccessResponse & {
  addition: string;
  houseNumber: string;
  postcode: string;
};
export interface AddressWithApiCallProps extends AddressProps {
  addition?: AddressWithApiCallInputProps;
  houseNumber?: AddressWithApiCallInputProps;
  postcode?: AddressWithApiCallInputProps;
  apiSuccessCallback?: (fieldValues: ApiSuccessParam) => void;
}

const getNewValue =
  ({
    validator,
    transformers,
  }: Pick<AddressWithApiCallInputProps, 'transformers' | 'validator'>) =>
  (
    e: React.ChangeEvent<HTMLInputElement>
  ): { newValue: string; error?: string } => {
    let newValue = e.target.value;
    const transformersFn = parseTransformer(transformers, transformersMap);
    for (const { transformerFn } of transformersFn) {
      newValue = transformerFn(newValue);
    }
    const errors = validate(
      {
        validator,
        value: newValue,
        active: true,
      },
      { validators }
    );
    const actualErrorMessage =
      newValue !== undefined && errors ? errors.join('. ') : undefined;
    return { newValue, error: actualErrorMessage };
  };

export class AddressApiError extends Error {
  constructor(message) {
    super(message);
    this.name = 'unknown combination';
  }
}

export const houseNumberError = new AddressApiError(
  'Deze combinatie van postcode/\u200bhuisnummer kennen we helaas niet.'
);

type AddressApiSuccessResponse = {
  city: string;
  street: string;
  fullAddress: string;
};

type AddressInputSetter = React.Dispatch<
  React.SetStateAction<AddressWithApiCallInputProps | undefined>
>;

const getOnChange =
  (setter: AddressInputSetter, props?: AddressWithApiCallInputProps) =>
  (e: React.ChangeEvent<HTMLInputElement>) => {
    if (props?.onChange) props.onChange(e);
    const { newValue } = validateField(e, props);
    setter((oldState) =>
      !oldState
        ? oldState
        : {
            ...oldState,
            value: newValue,
          }
    );
  };

const getOnBlur =
  (setter: AddressInputSetter, props?: AddressWithApiCallInputProps) =>
  (e: React.FocusEvent<HTMLInputElement>) => {
    if (props?.onBlur) props.onBlur(e);
    const { error } = validateField(e, props);
    setter((oldState) =>
      !oldState
        ? oldState
        : {
            ...oldState,
            _error: error,
          }
    );
  };

const validateField = (
  e: React.ChangeEvent<HTMLInputElement>,
  props?: AddressWithApiCallInputProps
) => {
  return getNewValue({
    validator: props?.validator,
    transformers: props?.transformers,
  })(e);
};

export const AddressWithApiCall: React.FC<AddressWithApiCallProps> = ({
  city,
  iconProps,
  pTextVariant,
  street,
  postcode,
  houseNumber,
  addition,
  apiSuccessCallback: apiResponseCallback = () => {
    /* do nothing */
  },
  variation = 'default',
  ...divProps
}) => {
  const form = useFormService();
  const houseNumberField = useField('housenumber', form);
  const [apiResult, setApiResult] = useState<
    Partial<AddressApiSuccessResponse>
  >({ city, street });
  // this wrapper has its own local state because it can be placed on multiple steps so we don't want it to be Wizard dependant
  // plus most of the time we don't want to actually update the wizard context onChange (except for the StartWidget page) but at specific events (save button clicked for example)
  const [postcodeProps, setPostcodeProps] = useState(postcode);
  const [houseNumberProps, setHouseNumberProps] = useState(houseNumber);
  const [additionProps, setAdditionProps] = useState(addition);
  const [additionPrev, setAdditionPrev] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { api } = useContext(ComparatorContext);
  const noErrors = useMemo(() => {
    if (!postcodeProps || !houseNumberProps) return false;
    return [postcodeProps, houseNumberProps].every(
      (props) => props._error === undefined && props.value
    );
  }, [postcodeProps, houseNumberProps]);
  const errorCallbackValues = useMemo(
    () => ({
      city: '',
      street: '',
      fullAddress: '',
      postcode: postcodeProps?.value as string,
      houseNumber: houseNumberProps?.value as string,
      addition: additionProps?.value as string,
    }),
    [postcodeProps?.value, houseNumberProps?.value, additionProps?.value]
  );
  const makeApiCall = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await api.validateAddress(
        postcodeProps?.value + '',
        houseNumberProps?.value + '',
        additionProps?.value ? additionProps?.value.toString() : ''
      );
      setApiResult(result);
      if (result) {
        setIsLoading(false);
      }
      houseNumberField?.set({
        meta: { isValid: true },
      });
      apiResponseCallback({
        ...result,
        postcode: postcodeProps?.value as string,
        houseNumber: houseNumberProps?.value as string,
        addition: additionProps?.value as string,
      });
    } catch (e) {
      setIsLoading(false);
      if ((e as AddressApiError).name === 'unknown combination') {
        setHouseNumberProps((v) => ({
          ...v,
          _error: (e as AddressApiError).message,
        }));
        setApiResult({
          city: undefined,
          street: undefined,
          fullAddress: undefined,
        });
        houseNumberField?.set({
          meta: { isValid: false },
        });
        apiResponseCallback(errorCallbackValues);
      } else {
        setIsLoading(false);
        KiziLogger.logError({
          eventId: 0,
          member: 'AddressWithApiCall',
          message: 'Error in makeApiCall',
          eventName: 'makeApiCallError',
          data: {
            error: e,
          },
        });
        throw e;
      }
    }
  }, [postcodeProps, houseNumberProps, additionProps, api]);

  const getExtendedProps = useCallback(
    (props, setter) => ({
      ...props,
      onChange: getOnChange(setter, props),
      onFocus: (e) => {
        if (props.onFocus) props.onFocus(e);
        setter((state) => ({ ...state, _error: undefined }));
      },
      onBlur: (e) => {
        getOnBlur(setter, props)(e);
        if (
          noErrors ||
          (additionPrev !== additionProps?.value && additionPrev !== '')
        ) {
          setAdditionPrev(additionProps?.value as string);
          setHouseNumberProps((v) => ({
            ...v,
            _error: undefined,
          }));
          makeApiCall();
        } else {
          setApiResult({
            city: undefined,
            street: undefined,
            fullAddress: undefined,
          });
          apiResponseCallback(errorCallbackValues);
        }
      },
    }),
    [noErrors, makeApiCall, apiResponseCallback]
  );

  const postcodePropsWithOnChange = getExtendedProps(
    { ...postcodeProps, _error: postcodeProps?._error ?? postcode?._error },
    setPostcodeProps
  );
  const houseNumberPropsWithOnChange = getExtendedProps(
    {
      ...houseNumberProps,
      _error: houseNumberProps?._error ?? houseNumber?._error,
    },
    setHouseNumberProps
  );
  const additionPropsWithOnChange = getExtendedProps(
    { ...additionProps, _error: additionProps?._error ?? addition?._error },
    setAdditionProps
  );
  return (
    <Address
      {...divProps}
      variation={variation}
      iconProps={iconProps}
      pTextVariant={pTextVariant}
      city={apiResult?.city}
      street={apiResult?.street}
      postcode={postcodePropsWithOnChange}
      houseNumber={houseNumberPropsWithOnChange}
      addition={additionPropsWithOnChange}
      loading={isLoading}
    />
  );
};
